/**
 * «Собрать сцепку» (AK-747, форма 2 макета `machinery-forms`).
 *
 * Состав — МАРКИ каталога: одно энергосредство (трактор, комбайн, самоходный
 * опрыскиватель) и 1..N прицепных машин с количеством. Количество — это
 * «сколько одинаковых машин в агрегате» (колонка книги «кол-во с/х машин»:
 * две бороны за одним трактором — обычная строка), а не «сколько раз собрали
 * сцепку».
 *
 * Имя генерится из состава и показывается сразу — тем же правилом, что на
 * сервере. Правил руками — отправляем как заданное; не правил — сервер соберёт
 * его сам, и оно продолжит следовать за составом.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Stack } from '@/components/ui/stack';
import { cn } from '@/lib/utils';
import { useMachineryCatalog, type CatalogModel } from '../queries';
import { modelName } from '../AddMachineryDialog';
import { previewUnitName, unitPayload, type UnitDraftMember } from './unitsLogic';

export function UnitDialog({
  open,
  pending,
  onCancel,
  onSave,
}: {
  open: boolean;
  pending: boolean;
  onCancel: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<boolean>;
}) {
  const [primeSearch, setPrimeSearch] = useState('');
  const [prime, setPrime] = useState<CatalogModel | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [members, setMembers] = useState<UnitDraftMember[]>([]);
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPrimeSearch('');
    setPrime(null);
    setMemberSearch('');
    setMembers([]);
    setName('');
    setNameTouched(false);
    setError(null);
  }, [open]);

  const primeCatalog = useMachineryCatalog(primeSearch, open);
  const memberCatalog = useMachineryCatalog(memberSearch, open);

  const generated = useMemo(
    () => previewUnitName(prime ? modelName(prime) : null, members),
    [prime, members],
  );
  const shownName = nameTouched ? name : generated;

  const addMember = (model: CatalogModel) => {
    setMembers((prev) =>
      prev.some((m) => m.model_id === model.id)
        ? prev
        : [...prev, { model_id: model.id, count: 1, name: modelName(model) }],
    );
    setMemberSearch('');
  };

  const submit = async () => {
    if (!prime && members.length === 0) {
      setError('Сцепка не бывает пустой — выберите энергосредство или прицепные машины.');
      return;
    }
    setError(null);
    await onSave(
      unitPayload({
        // Компанию сервер определит сам, если она в тенанте одна; в холдинге
        // с несколькими он честно попросит уточнить.
        primeModelId: prime?.id ?? null,
        members,
        name: shownName,
        nameTouched,
        operationIds: [],
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Собрать сцепку</DialogTitle>
          <DialogDescription>
            Марки из каталога, а не конкретные машины: норматив выработки
            ключуется маркой агрегата. Какая именно машина работала — пишется на
            операцию.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={4}>
          <div>
            <Label htmlFor="unit-prime">Энергосредство (трактор / самоходная машина)</Label>
            {prime ? (
              <div className="mt-1 flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                <span className="flex-1 font-medium">{modelName(prime)}</span>
                <Button variant="ghost" size="sm" onClick={() => setPrime(null)}>
                  Изменить
                </Button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="unit-prime"
                    className="pl-8"
                    value={primeSearch}
                    placeholder="Поиск по каталогу: К-744, МТЗ, ACROS…"
                    onChange={(e) => setPrimeSearch(e.target.value)}
                  />
                </div>
                <ScrollArea className="mt-2 h-32 rounded-md border border-border">
                  <ul data-testid="unit-prime-list">
                    {(primeCatalog.data?.items ?? []).map((model) => (
                      <li key={model.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => setPrime(model)}
                        >
                          {modelName(model)}
                          {model.type_name && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {model.type_name}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
                <p className="mt-1 text-xs text-muted-foreground">
                  Самоходный комбайн или опрыскиватель — тоже сюда, прицепных
                  тогда просто нет.
                </p>
              </>
            )}
          </div>

          <div>
            <Label>Прицепные машины</Label>
            <Stack gap={2} className="mt-1">
              {members.map((member, idx) => (
                <div
                  key={member.model_id}
                  className="flex items-center gap-2 rounded-md border border-border p-2 text-sm"
                >
                  <span className="text-muted-foreground">{idx + 1}</span>
                  <span className="flex-1">{member.name}</span>
                  <Label htmlFor={`count-${member.model_id}`} className="text-xs">
                    количество
                  </Label>
                  <Input
                    id={`count-${member.model_id}`}
                    className="w-16"
                    inputMode="numeric"
                    value={String(member.count)}
                    onChange={(e) =>
                      setMembers((prev) =>
                        prev.map((m) =>
                          m.model_id === member.model_id
                            ? { ...m, count: Math.max(1, Number(e.target.value) || 1) }
                            : m,
                        ),
                      )
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Убрать ${member.name}`}
                    onClick={() =>
                      setMembers((prev) => prev.filter((m) => m.model_id !== member.model_id))
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </Stack>

            <div className="relative mt-2">
              <Plus className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                value={memberSearch}
                placeholder="Добавить машину в сцепку — поиск по каталогу"
                onChange={(e) => setMemberSearch(e.target.value)}
                data-testid="unit-member-search"
              />
            </div>
            {memberSearch.trim() && (
              <ScrollArea className="mt-2 h-32 rounded-md border border-border">
                <ul data-testid="unit-member-list">
                  {(memberCatalog.data?.items ?? []).map((model) => (
                    <li key={model.id}>
                      <button
                        type="button"
                        className={cn('w-full px-3 py-2 text-left text-sm hover:bg-muted')}
                        onClick={() => addMember(model)}
                      >
                        {modelName(model)}
                        {model.type_name && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {model.type_name}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Количество — сколько одинаковых машин в агрегате: две бороны за
              одним трактором это одна сцепка, а не две.
            </p>
          </div>

          <div>
            <Label htmlFor="unit-name">Название сцепки</Label>
            <Input
              id="unit-name"
              className="mt-1"
              value={shownName}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
              }}
              data-testid="unit-name"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {nameTouched
                ? 'Название задано вручную — за составом оно больше не следует.'
                : 'Собирается из состава. Уникально в компании.'}
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </Stack>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button disabled={pending} onClick={() => void submit()} data-testid="unit-save">
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить сцепку
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
